import {
  ContentState,
  GlobalNewsCardProps,
  NewsItemCardProps,
  NewsListProps,
} from "@/src/models/news.model";
import { useTheme } from "@/src/styles/theme-provider";
import { LinearGradient } from "expo-linear-gradient";
import { memo, useCallback } from "react";
import { useTranslation } from "react-i18next";
import {
  Image,
  Linking,
  ScrollView,
  Text,
  TouchableOpacity,
  View,
} from "react-native";
import { GlobalNewsSkeleton } from "./loading-skeleton/news-card-skeleton";

const CATEGORY_BG_CLASSES = [
  "bg-blush",
  "bg-lavender",
  "bg-sage",
  "bg-wheat",
  "bg-coral",
] as const;

const getCategoryBg = (seed: number) =>
  CATEGORY_BG_CLASSES[seed % CATEGORY_BG_CLASSES.length];

const LoadingFailedImage = require("@/src/assets/images/Loading-failed.png");

const LoadingState = memo(function LoadingState() {
  return <GlobalNewsSkeleton />;
});

const ErrorState = memo(function ErrorState() {
  const { t } = useTranslation();
  return (
    <View className="flex-1 justify-center items-center">
      <Image
        source={LoadingFailedImage}
        style={{ width: 100, height: 100 }}
        resizeMode="contain"
      />
      <Text className="text-crimson">{t("ERROR_NEWS_LOADING")}</Text>
      <Text className="text-ash-1 text-sm text-center mt-2">
        {t("HINT_LOADING_FAILED")}
      </Text>
    </View>
  );
});

const NewsItemCard = memo(function NewsItemCard({
  item,
  index,
}: NewsItemCardProps) {
  const { themeVars } = useTheme();
  const handlePress = useCallback(() => {
    Linking.openURL(item.link);
  }, [item.link]);

  return (
    <TouchableOpacity
      className="border border-ash-3 rounded-2xl p-3 gap-1 hover:bg-ash-3"
      activeOpacity={0.7}
      onPress={handlePress}
    >
      <Text
        numberOfLines={2}
        ellipsizeMode="tail"
        className="text-sm font-bold text-onyx leading-snug"
      >
        {item.title}
      </Text>

      <View className="relative">
        <Text
          className="text-xs text-ash-1 leading-relaxed"
          numberOfLines={3}
          ellipsizeMode="tail"
        >
          {item.description}
        </Text>
        <LinearGradient
          colors={["transparent", themeVars["--color-chalk-default"] as string]}
          className="absolute bottom-0 left-0 right-0 h-4"
        />
      </View>

      <View className="flex-row items-center gap-2 flex-wrap mt-1">
        <Text className="text-xs text-ash-1">{item.date}</Text>
        {item.category.map((cat, idx) => (
          <View
            key={idx}
            className={`${getCategoryBg(index + idx)} px-2 py-1 rounded-2xl`}
          >
            <Text className="text-xs font-medium text-slate">{cat}</Text>
          </View>
        ))}
      </View>
    </TouchableOpacity>
  );
});

const NewsList = memo(function NewsList({ news }: NewsListProps) {
  return (
    <ScrollView
      className="flex-1"
      contentContainerClassName="gap-3"
      showsVerticalScrollIndicator={false}
    >
      {news.map((item, index) => (
        <NewsItemCard key={index} item={item} index={index} />
      ))}
    </ScrollView>
  );
});

function resolveContentState(
  isLoading: boolean,
  isError: boolean,
): ContentState {
  if (isLoading) return "loading";
  if (isError) return "error";
  return "data";
}

export default function GlobalNewsCard({
  data,
  isLoading,
  isError,
}: GlobalNewsCardProps) {
  const { t } = useTranslation();
  const contentState = resolveContentState(isLoading, isError);

  const contentMap: Record<ContentState, React.ReactNode> = {
    loading: <LoadingState />,
    error: <ErrorState />,
    data: <NewsList news={data?.news ?? []} />,
  };

  return (
    <View className="flex-col border border-ash-3 rounded-2xl bg-chalk md:w-[26%] p-4">
      <Text className="text-sm font-bold text-onyx mb-3">
        {t("LABEL_GLOBAL_NEWS")}
      </Text>
      {contentMap[contentState]}
    </View>
  );
}
